import { useState } from "react";
import { Box, Button, Stack } from "@mui/material";
import { Add } from "@mui/icons-material";
import {
  ColumnVisibilityFilter,
  SearchFilter,
  SectionHeader,
} from "@shared/components";
import { FormikHelpers } from "formik";
import { enqueueSnackbar } from "notistack";
import {
  ColumnKey,
  FilterField,
  InvoiceFormValues,
  InvoiceHeaderProps,
} from "../utils/types";
import InvoiceForm from "./InvoiceForm";
import { ColumnOptions } from "../utils/constants";
import { FormActions } from "@shared/utils/constant";
import { useCreateInvoiceParty } from "../services/useCreateInvoiceParty";
import { Can } from "@core/casl-provider";

const filterFields: FilterField[] = [
  {
    id: "invoice-search",
    name: "searchQuery",
    label: "Search Invoices",
    placeholder: "Search invoices...",
    clearAriaLabel: "clear invoice search",
  },
];

const InvoiceHeader = ({
  visibleColumns,
  setVisibleColumns,
}: InvoiceHeaderProps) => {
  const [actionState, setActionState] = useState<{
    action: FormActions.ADD | "";
    payload: InvoiceFormValues | object;
  }>({
    action: "",
    payload: {} as InvoiceFormValues,
  });

  const { mutateAsync: createInvoicePartiesAsync, isPending } =
    useCreateInvoiceParty();

  const clearActionState = () => {
    setActionState({ action: "", payload: {} });
  };
  const handleSubmit = async (
    values: InvoiceFormValues,
    helpers: FormikHelpers<InvoiceFormValues & { id?: number }>
  ) => {
    try {
      await createInvoicePartiesAsync({ payload: values });
      clearActionState();
    } catch (error) {
      const errMsg = (error as Error)?.message || "Something went wrong";
      enqueueSnackbar(errMsg, {
        variant: "error",
        autoHideDuration: 5000,
      });
    } finally {
      helpers.setSubmitting(false);
    }
  };
  return (
    <>
      <Box>
        <SectionHeader title="Invoice Parties" />
        <Stack
          direction="row"
          spacing={2}
          justifyContent="space-between"
          mt={3}
          alignItems="center"
          sx={(theme) => ({
            border: `1px solid ${theme.palette.divider}`,
            borderBottom: 0,
            backgroundColor: "background.paper",
            padding: "15px",
            borderRadius: "4px 4px 0 0",
          })}
        >
          <SearchFilter filterFields={filterFields} />

          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
          >
            <ColumnVisibilityFilter
              value={visibleColumns as string[]}
              onChange={(vals) => setVisibleColumns(vals as ColumnKey[])}
              options={ColumnOptions}
              size="medium"
            />

            <Can
              do={"create"}
              on={"InvoiceParty"}
            >
              <Button
                variant="contained"
                sx={{ height: 38, padding: "0 20px" }}
                onClick={() =>
                  setActionState({ action: FormActions.ADD, payload: {} })
                }
                startIcon={<Add />}
              >
                Add New Invoice Party
              </Button>
            </Can>
          </Stack>
        </Stack>
      </Box>

      <InvoiceForm
        open={actionState.action === FormActions.ADD}
        onClose={clearActionState}
        title="Add Invoice Party"
        initialValues={actionState.payload}
        disable={isPending}
        onSubmit={handleSubmit}
        showCloseButton
      />
    </>
  );
};

export default InvoiceHeader;
